import React from 'react';
import { curveValueAt, spreadMarkers, todayMidMinute } from './iconMarkers';
import {
  DAY_NIGHT_FILL,
  DAY_NIGHT_FILL_CURRENT,
  buildDayLabelSegments,
  buildDayNightSegments,
  currentCycleIndex,
} from '../character_stats/powerCurveUtil';

// PiP 창에서 쓰는 파워 커브 차트(Recharts 대신 순수 SVG). 한 그래프에 여러 선을 색으로 구분해 겹쳐 그린다.
// series: [{ key, color, faded, points: [{ time_bucket, smoothed_zscore }] }] — faded는 비교용 기준선(연하고 가는 점선)
// showIcons: 켜면 지금 낮/밤 구간(강조 구간) 가운데에 각 캐릭터 아이콘을 그 선 위에 띄운다(웹 우리 팀 카드용, series[].icon/name 필요).
// current: 지금 { day, phase } — 그 일차의 낮/밤 구간을 진한 배경과 테두리로, 일차 숫자를 진하게 강조한다(일차를 모르면 강조 없음).

const WIDTH = 640;
const HEIGHT = 230;
const PAD = { left: 40, right: 12, top: 10, bottom: 8 };

// 연속된 분(time_bucket)끼리만 이어 그린다 (표본이 부족한 구간은 선을 끊는다).
export function toRuns(points) {
  const sorted = points
    .filter((p) => p.smoothed_zscore !== null && p.smoothed_zscore !== undefined)
    .sort((a, b) => a.time_bucket - b.time_bucket);
  const runs = [];
  sorted.forEach((p) => {
    const last = runs[runs.length - 1];
    if (last && p.time_bucket - last[last.length - 1].time_bucket === 1) {
      last.push(p);
    } else {
      runs.push([p]);
    }
  });
  return runs;
}

export default function PowerCurveSvg({ series, current = null, showIcons = false }) {
  const all = series.flatMap((s) => s.points).filter((p) => p.smoothed_zscore !== null && p.smoothed_zscore !== undefined);
  if (!all.length) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>파워 커브 데이터가 없어요.</div>;
  }
  const maxBucket = Math.max(...all.map((p) => p.time_bucket));
  const minVal = Math.min(...all.map((p) => p.smoothed_zscore), 0);
  const maxVal = Math.max(...all.map((p) => p.smoothed_zscore), 0);
  const pad = (maxVal - minVal) * 0.08 || 0.1;
  const yMin = minVal - pad;
  const yMax = maxVal + pad;

  const currentIndex = currentCycleIndex(current);
  const currentDay = currentIndex === null ? null : current.day;

  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (minute) => PAD.left + ((minute - 1) / Math.max(maxBucket - 1, 1)) * plotW;
  const y = (value) => PAD.top + ((yMax - value) / (yMax - yMin)) * plotH;

  // 지금 낮/밤 구간 가운데의 아이콘: 가운데 분에서 각 선의 값 위치에 놓고, 겹치면 가로로 밀어낸다
  const iconRadius = 11;
  const midMinute = showIcons ? todayMidMinute(current, maxBucket) : null;
  const iconMarkers = midMinute === null ? [] : spreadMarkers(
    series
      .filter((s) => !s.faded && s.icon)
      .map((s) => ({ key: s.key, series: s, value: curveValueAt(s.points, midMinute) }))
      .filter((m) => m.value !== null)
      .map((m) => ({ key: m.key, x: x(midMinute), y: y(m.value) })),
    iconRadius * 2 + 2,
    { xMin: PAD.left + iconRadius, xMax: WIDTH - PAD.right - iconRadius }
  );

  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" role="img" aria-label="파워 커브" style={{ display: 'block' }}>
      {buildDayNightSegments(maxBucket).map((seg) => {
        const isCurrent = seg.cycleIndex === currentIndex;
        return (
          <rect
            key={`${seg.phase}-${seg.x1}`}
            x={x(seg.x1)}
            y={PAD.top}
            width={Math.max(x(seg.x2) - x(seg.x1), 0)}
            height={plotH}
            fill={(isCurrent ? DAY_NIGHT_FILL_CURRENT : DAY_NIGHT_FILL)[seg.phase]}
            stroke={isCurrent ? '#1677ff' : undefined}
            strokeWidth={isCurrent ? 1.5 : undefined}
          />
        );
      })}
      {buildDayLabelSegments(maxBucket).map((seg) => {
        const isCurrentDay = currentDay !== null && seg.day === currentDay;
        return (
          <text
            key={`day-${seg.day}`}
            x={x(seg.labelX)}
            y={PAD.top + plotH / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="34"
            fontWeight="800"
            fill={isCurrentDay ? '#1677ff' : '#4a4a4a'}
            fillOpacity={isCurrentDay ? 0.4 : 0.14}
          >
            {seg.day}
          </text>
        );
      })}
      <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(0)} y2={y(0)} stroke="#999" strokeDasharray="4 4" strokeWidth="1" />
      <text x={PAD.left - 6} y={y(yMax)} textAnchor="end" dominantBaseline="hanging" fontSize="11" fill="#8c8c8c">{yMax.toFixed(1)}</text>
      <text x={PAD.left - 6} y={y(0)} textAnchor="end" dominantBaseline="central" fontSize="11" fill="#8c8c8c">0</text>
      <text x={PAD.left - 6} y={y(yMin)} textAnchor="end" dominantBaseline="auto" fontSize="11" fill="#8c8c8c">{yMin.toFixed(1)}</text>
      {series.map((s) => toRuns(s.points).map((run) => (
        <polyline
          key={`${s.key}-${run[0].time_bucket}`}
          fill="none"
          stroke={s.color}
          strokeWidth={s.faded ? 1.6 : 2.2}
          strokeOpacity={s.faded ? 0.32 : 1}
          strokeDasharray={s.faded ? '5 4' : undefined}
          strokeLinejoin="round"
          points={run.map((p) => `${x(p.time_bucket).toFixed(1)},${y(p.smoothed_zscore).toFixed(1)}`).join(' ')}
        />
      )))}
      {iconMarkers.map((marker, i) => {
        const source = series.find((s) => s.key === marker.key);
        return (
          <g key={`icon-${marker.key}`} transform={`translate(${marker.x.toFixed(1)},${marker.y.toFixed(1)})`}>
            <clipPath id={`power-curve-icon-${i}`}>
              <circle r={iconRadius} />
            </clipPath>
            <circle r={iconRadius + 1.5} fill="#fff" stroke={source.color} strokeWidth="2" />
            <image
              href={source.icon}
              x={-iconRadius}
              y={-iconRadius}
              width={iconRadius * 2}
              height={iconRadius * 2}
              preserveAspectRatio="xMidYMin slice"
              clipPath={`url(#power-curve-icon-${i})`}
            >
              <title>{source.name}</title>
            </image>
          </g>
        );
      })}
    </svg>
  );
}
